// Dashboard.js - the workspace's home: a welcome, the headline numbers, the latest simulations, the learners' plays by day,
// quick actions and recent activity. Everything comes from the org's real data: GET /overview (backend services/overview.py)
// for simulations and plays, the members and the audit trail for the getting-started steps and the activity list.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, Box, CheckCircle2, ChevronRight, Circle, Library, PlusCircle, UserPlus, Users } from "lucide-react";
import SkeletonLoader from "./common/SkeletonLoader";
import { getOverview } from "../game-builder/api/gameBuilderAPI";
import { listAuditEvents, listMembers } from "../api/studioAPI";
import { useProfile, isAdmin, isOwner } from "../context/ProfileContext";
import { displayName } from "./Header";
import { HeadlineStats, SimPicture, StatusChip, ago, pct } from "./dashboard/overviewParts";
import ProgressChart from "./dashboard/ProgressChart";
import "./dashboard/Dashboard.css";

const ACTIONS = { CREATE: "created", DELETE: "deleted", PUBLISH: "published", UNPUBLISH: "unpublished", INVITE: "invited",
  JOIN: "joined", ROLE: "changed the role of", REMOVE: "removed", UPDATE: "updated", EXPORT: "exported" };

// A plan or simulation is named by its title (`names`, from titlesOf) - never its id; one that's gone (deleted) is "a plan".
export const describe = (e, names = {}) => {
  const who = (e.actor || "someone").split("@")[0];
  const verb = ACTIONS[e.action] || e.action.toLowerCase();
  if (e.entity_type === "org") return `${who} ${verb} the organization`;
  if (e.entity_type === "plan" || e.entity_type === "game") {
    const kind = e.entity_type === "plan" ? "plan" : "simulation";
    const name = names[e.entity_id];
    return name ? `${who} ${verb} ${kind} “${name}”` : `${who} ${verb} a ${kind}`;
  }
  return `${who} ${verb} ${e.entity_id}`.replace(/\s+/g, " ").trim();
};
const short = (text, max = 60) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);
// {plan id: its title, "plan/mission": the simulation's title} - from the overview's simulations (GET /overview)
export const titlesOf = (simulations = []) => Object.fromEntries(simulations.flatMap((s) => [
  [s.plan_id, short(s.plan || "")], [`${s.plan_id}/${s.mission_id}`, short(s.title || "")]]).filter(([, t]) => t));
export const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

const PLAYED = { goal: "completed", fail: "didn't succeed at", dead_end: "didn't succeed at", left: "left" };

const planLink = (planId) => `/app/studio?plan=${encodeURIComponent(planId)}`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([getOverview(), listMembers().catch(() => ({ items: [] })), listAuditEvents(50).catch(() => ({ items: [] }))])
      .then(([overview, members, audit]) => setData({ ...overview, members: members.items, events: audit.items }))
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <p role="alert" style={{ color: "#c0392b" }}>{error}</p>;
  if (!data) return <SkeletonLoader type="dashboard" count={3} />;

  const sims = data.simulations;
  const canInvite = isOwner(profile) && !profile?.org?.is_demo;
  const steps = [
    { done: sims.length > 0, label: "Make a plan from one of your procedures", go: () => navigate("/app/studio?new=1") },
    { done: data.totals.published > 0, label: "Build a simulation and share its link", go: () => navigate("/app/studio") },
    ...(canInvite ? [{ done: data.members.length > 1, label: "Invite a colleague", go: () => navigate("/app/settings?section=members") }] : []),
  ];
  const activity = [
    ...data.recent_plays.map((p) => ({ at: p.at, kind: p.outcome === "goal" ? "won" : "played",
      text: <><strong>{p.name || "A learner"}</strong> {PLAYED[p.outcome]} {p.title}</> })),
    ...data.events.map((e) => ({ at: e.at, kind: e.action === "PUBLISH" ? "published" : "edit", text: describe(e, titlesOf(data.simulations)) })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || "")).slice(0, 6);
  const anyPlays = data.by_day.some((d) => d.won + d.not_won + d.left > 0);

  return (
    <div className="db">
      <section className="db-hero">
        <img src="/assets/landing/hero-warehouse.jpg" alt="" className="db-hero-img" />
        <div className="db-hero-text">
          <h1>Welcome back, {displayName(profile?.email).split(" ")[0]}</h1>
          <p>Build, share and track interactive job simulations.</p>
          <div className="db-hero-cta">
            <button type="button" className="db-btn db-btn-primary" onClick={() => navigate("/app/studio?new=1")}>
              Create Simulation <ArrowRight size={18} aria-hidden="true" />
            </button>
            <button type="button" className="db-btn db-btn-outline" onClick={() => navigate("/app/studio")}>My Simulations</button>
          </div>
        </div>
        <div className="db-hero-badge"><Box size={26} aria-hidden="true" /> Turn your procedures into practice.</div>
      </section>

      <HeadlineStats data={data} />

      <div className="db-columns">
        <div className="db-col">
          <section className="db-card db-panel">
            <div className="db-panel-head">
              <h2>Recent Simulations</h2>
              {sims.length > 0 && <button type="button" className="db-link" onClick={() => navigate("/app/studio")}>View all <ArrowRight size={16} /></button>}
            </div>
            {sims.length === 0 ? (
              <div className="db-empty">
                <p>No simulations yet. Start one from an SOP, a manual or a policy.</p>
                <button type="button" className="db-btn db-btn-primary" onClick={() => navigate("/app/studio?new=1")}>Create Simulation</button>
              </div>
            ) : (
              <div className="db-sims">
                {sims.slice(0, 4).map((s) => (
                  <button key={`${s.plan_id}/${s.mission_id}`} type="button" className="db-sim" onClick={() => navigate(planLink(s.plan_id))}>
                    <SimPicture src={s.picture} />
                    <div className="db-sim-body">
                      <strong className="db-sim-title">{s.title}</strong>
                      <StatusChip status={s.status} />
                      <div className="db-sim-nums">
                        <span title="Learners"><Users size={15} aria-hidden="true" /> {s.learners}</span>
                        <span title="Success rate"><BarChart3 size={15} aria-hidden="true" /> {pct(s.success)}</span>
                      </div>
                      <div className="db-muted">{s.updated_at ? `${s.published_at ? "Published" : "Created"} ${ago(s.updated_at)}` : ""}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="db-card db-panel">
            <div className="db-panel-head">
              <h2>Learner Progress</h2>
              <span className="db-muted">Last {data.days} days</span>
            </div>
            {anyPlays ? <ProgressChart days={data.by_day} /> : (
              <div className="db-empty"><p>No plays in the last {data.days} days. Publish a simulation and share its link with your learners.</p></div>
            )}
          </section>
        </div>

        <div className="db-col">
          <section className="db-card db-panel">
            <h2>Quick Actions</h2>
            <div className="db-actions">
              <button type="button" className="db-action db-action-primary" onClick={() => navigate("/app/studio?new=1")}>
                <PlusCircle size={20} aria-hidden="true" /> Create Simulation <ChevronRight size={18} aria-hidden="true" />
              </button>
              {isAdmin(profile) && (
                <button type="button" className="db-action" onClick={() => navigate("/app/items")}>
                  <Library size={20} aria-hidden="true" /> Browse the Library <ChevronRight size={18} aria-hidden="true" />
                </button>
              )}
              {canInvite && (
                <button type="button" className="db-action" onClick={() => navigate("/app/settings?section=members")}>
                  <UserPlus size={20} aria-hidden="true" /> Invite Colleagues <ChevronRight size={18} aria-hidden="true" />
                </button>
              )}
              <button type="button" className="db-action" onClick={() => navigate("/app/results")}>
                <BarChart3 size={20} aria-hidden="true" /> View Results <ChevronRight size={18} aria-hidden="true" />
              </button>
            </div>
          </section>

          {steps.some((s) => !s.done) && (
            <section className="db-card db-panel">
              <h2>Getting Started</h2>
              {steps.map((s) => (
                <button key={s.label} type="button" onClick={s.go} className={`db-step${s.done ? " db-step-done" : ""}`}>
                  {s.done ? <CheckCircle2 size={18} aria-hidden="true" /> : <Circle size={18} aria-hidden="true" />} {s.label}
                </button>
              ))}
            </section>
          )}

          <section className="db-card db-panel">
            <div className="db-panel-head">
              <h2>Recent Activity</h2>
              {data.events.length > 0 && <button type="button" className="db-link" onClick={() => navigate("/app/audit")}>View all <ArrowRight size={16} /></button>}
            </div>
            {activity.length === 0 ? <p className="db-muted">Nothing yet.</p> : (
              <ul className="db-activity">
                {activity.map((a, i) => (
                  <li key={`${a.at}-${i}`}>
                    <i className={`db-dot db-dot-${a.kind}`} aria-hidden="true" />
                    <span className="db-activity-text">{a.text}</span>
                    <span className="db-muted db-activity-at" title={when(a.at)}>{ago(a.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
